import type React from 'react';
import { memo, type MouseEvent, useMemo, useState } from 'react';

import * as styles from './styles';
import { ConfirmationStep } from './steps/ConfirmationStep/ConfirmationStep';
import { MediaTypeSelectionStep } from './steps/MediaTypeSelectionStep/MediaTypeSelectionStep';
import { PeriodSelectionStep } from './steps/PeriodSelectionStep/PeriodSelectionStep';
import { VehicleSelectionStep } from './steps/VehicleSelectionStep/VehicleSelectionStep';
import { Button, Divider } from '@cobliteam/ui/components';
import lodash from 'lodash';

import { type CreateMediaRequest, MediaType } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import { useCameraTrip } from '@cobliteam/hooks';
import { useCreateMediaRequest, useGetMediaRequestList } from '@cobliteam/hooks/src/useCamera/useCameraMedia';

import { Breadcrumbs, ConfirmationPopper, DialogWithHeader } from '@cobliteam/base-components';

import Icon from '@cobliteam/ui/icons';

import Alert from '@cobliteam/ui/components/Alert';

import useMediaStore from '../../store/zustand/useMediaStore';
import type { MediaRequestData } from '../../types/MediaRequest';
import {
  DEFAULT_MEDIA_REQUEST_DATA,
  DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY,
  DEFAULT_MEDIA_REQUEST_STATUS,
  getPeriodErrorMessage,
  getRequestPeriod,
  MEDIA_REQUEST_MODAL_BASE_PERIOD,
  MEDIA_REQUEST_MODAL_MEDIAS_LIMIT,
  MEDIA_REQUEST_MODAL_TRIPS_LIMIT,
} from '../../utils/mediaUtils';

export interface MediaRequestModalProps {
  onClose: (refresh?: boolean) => void;
}

export enum MediaRequestModalSteps {
  VEHICLE_SELECTION = 'Selecionar veículo',
  MEDIA_TYPE_SELECTION = 'Escolher categoria do vídeo',
  PERIOD_SELECTION = 'Selecionar data',
  REQUEST_CONFIRMATION = 'Confirmar solicitação',
}

const steps = [
  {
    id: MediaRequestModalSteps.VEHICLE_SELECTION,
    title: `1. ${MediaRequestModalSteps.VEHICLE_SELECTION}`,
    disabledClick: true,
  },
  {
    id: MediaRequestModalSteps.MEDIA_TYPE_SELECTION,
    title: `2. ${MediaRequestModalSteps.MEDIA_TYPE_SELECTION}`,
    disabledClick: true,
  },
  {
    id: MediaRequestModalSteps.PERIOD_SELECTION,
    title: `3. ${MediaRequestModalSteps.PERIOD_SELECTION}`,
    disabledClick: true,
  },
  {
    id: MediaRequestModalSteps.REQUEST_CONFIRMATION,
    title: `4. ${MediaRequestModalSteps.REQUEST_CONFIRMATION}`,
    disabledClick: true,
  },
];

const MediaRequestModal: React.FC<MediaRequestModalProps> = memo(({ onClose }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [confirmClosePopperAnchor, setConfirmClosePopperAnchor] = useState<HTMLButtonElement | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const mediaRequestData = useMediaStore((state) => state.requestMediaData);
  const setMediaRequestData = useMediaStore((state) => state.setRequestMediaData);

  const { getCameraTrip } = useCameraTrip();
  const { data: tripsPagination } = getCameraTrip(
    mediaRequestData!.vehicleId!,
    MEDIA_REQUEST_MODAL_BASE_PERIOD.before.valueOf(),
    MEDIA_REQUEST_MODAL_BASE_PERIOD.after.valueOf(),
    DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY,
    MEDIA_REQUEST_MODAL_TRIPS_LIMIT.min,
    MEDIA_REQUEST_MODAL_TRIPS_LIMIT.max,
    { column: 'startTime', order: 'desc' },
    {
      refetchOnMount: 'always',
      enabled: !!mediaRequestData?.vehicleId && steps[currentStepIndex].id !== MediaRequestModalSteps.VEHICLE_SELECTION,
      staleTime: undefined,
    }
  );

  const { mutateAsync } = useCreateMediaRequest();
  const { data: mediasPage } = useGetMediaRequestList(
    {
      mediaPeriod: {
        start: MEDIA_REQUEST_MODAL_BASE_PERIOD.before.valueOf(),
        end: MEDIA_REQUEST_MODAL_BASE_PERIOD.after.valueOf(),
      },
      page: MEDIA_REQUEST_MODAL_MEDIAS_LIMIT.min,
      size: MEDIA_REQUEST_MODAL_MEDIAS_LIMIT.max,
      vehicleIds: mediaRequestData.vehicleId ? [mediaRequestData.vehicleId] : undefined,
      mediaTypes: [MediaType.FOOTAGE_VIDEO, MediaType.TIMELAPSE],
      requestStatus: DEFAULT_MEDIA_REQUEST_STATUS,
    },
    {
      refetchOnMount: 'always',
      enabled: !!mediaRequestData?.vehicleId && steps[currentStepIndex].id !== MediaRequestModalSteps.VEHICLE_SELECTION,
      staleTime: undefined,
    }
  );

  const selectedTrip = useMemo(
    () => tripsPagination?.content.find((trip) => trip.startTimestamp === mediaRequestData?.requestPeriodStart),
    [JSON.stringify(tripsPagination?.content), mediaRequestData?.requestPeriodStart]
  );

  const isStepValid = useMemo(() => {
    const step = steps[currentStepIndex];
    const { vehicleId, mediaType, requestDate, requestPeriodStart, requestMinute } = mediaRequestData as MediaRequestData;
    const isMinuteValid = (mediaType === MediaType.FOOTAGE_VIDEO && requestMinute) || mediaType === MediaType.TIMELAPSE;

    switch (step.id) {
      case MediaRequestModalSteps.VEHICLE_SELECTION:
        return !!vehicleId;
      case MediaRequestModalSteps.MEDIA_TYPE_SELECTION:
        return !!mediaType;
      case MediaRequestModalSteps.PERIOD_SELECTION:
        return !!requestPeriodStart && !!requestDate && isMinuteValid && !getPeriodErrorMessage(mediaRequestData, mediasPage?.content, selectedTrip);
      case MediaRequestModalSteps.REQUEST_CONFIRMATION:
        return true;
      default:
        return false;
    }
  }, [currentStepIndex, JSON.stringify(mediaRequestData), JSON.stringify(mediasPage?.content), JSON.stringify(selectedTrip)]);

  /* eslint-disable-next-line consistent-return */
  const onRequestButtonClick = async () => {
    const period = getRequestPeriod(mediaRequestData, selectedTrip);

    if (!period) {
      return undefined;
    }

    setIsSaving(true);

    const mediaRequest: CreateMediaRequest = {
      vehicleId: mediaRequestData?.vehicleId!,
      startTime: period.start,
      endTime: period.end,
      isTimelapse: mediaRequestData?.mediaType === MediaType.TIMELAPSE,
      enableEmailNotification: mediaRequestData?.hasNotificationEnabled,
      tripId: mediaRequestData.tripId,
    };

    Analytics.track('Media Requested Modal Confirmation Clicked', {
      vehicleId: mediaRequest.vehicleId,
      mediaType: mediaRequestData?.mediaType,
      periodStart: period.start,
      periodEnd: period.end,
      notificationEnabled: mediaRequest.enableEmailNotification,
    });

    try {
      mutateAsync(mediaRequest);
      Alert.success(
        mediaRequest.enableEmailNotification
          ? 'Solicitação de vídeo realizada com sucesso! Você receberá um e-mail quando estiver disponível.'
          : 'Solicitação de vídeo realizada com sucesso!'
      );
      onClose(true);
      setIsSaving(false);
      setMediaRequestData(DEFAULT_MEDIA_REQUEST_DATA);
    } catch {
      Alert.error('Ops! Houve um problema ao solicitar seu vídeo. Tente novamente.');

      setIsSaving(false);

      Analytics.track('Media Request Modal Failed', {
        vehicleId: mediaRequest.vehicleId,
        mediaType: mediaRequestData?.mediaType,
        periodStart: period.start,
        periodEnd: period.end,
        notificationEnabled: mediaRequest.enableEmailNotification,
      });
    }
  };

  const renderStepContent = () => {
    switch (steps[currentStepIndex].id) {
      case MediaRequestModalSteps.VEHICLE_SELECTION:
        return <VehicleSelectionStep />;
      case MediaRequestModalSteps.MEDIA_TYPE_SELECTION:
        return <MediaTypeSelectionStep />;
      case MediaRequestModalSteps.PERIOD_SELECTION:
        return <PeriodSelectionStep />;
      case MediaRequestModalSteps.REQUEST_CONFIRMATION:
        return <ConfirmationStep />;
      default:
        return null;
    }
  };

  const onNextStep = () => {
    if (steps[currentStepIndex].id === MediaRequestModalSteps.REQUEST_CONFIRMATION) {
      onRequestButtonClick();
    } else {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const onPreviousStep = () => {
    setCurrentStepIndex(currentStepIndex - 1);
  };

  const onCloseModal = (event: MouseEvent<HTMLButtonElement>) => {
    if (lodash.isEqual(mediaRequestData, DEFAULT_MEDIA_REQUEST_DATA)) {
      onClose(false);
    } else {
      setConfirmClosePopperAnchor(event.currentTarget);
    }
  };

  const onCancelClosePopper = () => {
    setConfirmClosePopperAnchor(undefined);

    Analytics.track('Media Request Modal Canceled');
  };

  const onConfirmClosePopper = () => {
    const period = getRequestPeriod(mediaRequestData, selectedTrip);

    setConfirmClosePopperAnchor(undefined);
    setMediaRequestData(DEFAULT_MEDIA_REQUEST_DATA);
    onClose(false);

    Analytics.track('Media Request Modal Cancellation Confirmed', {
      vehicleId: mediaRequestData?.vehicleId,
      mediaType: mediaRequestData?.mediaType,
      periodStart: period?.start,
      periodEnd: period?.end,
      notificationEnabled: mediaRequestData?.hasNotificationEnabled,
    });
  };

  return (
    <DialogWithHeader maxWidth='lg' open title='Solicitar vídeo' onCloseButtonClick={onCloseModal} scroll='body' className={styles.modalMargin}>
      <Breadcrumbs breadcrumbs={steps} selectedBreadcrumb={steps[currentStepIndex]} className={styles.breadcrumbs} />

      <div className={styles.stepContentWrapper}>{renderStepContent()}</div>

      <div>
        <Divider />
        <div className={styles.buttonGroup}>
          {steps[currentStepIndex].id !== MediaRequestModalSteps.VEHICLE_SELECTION && (
            <Button variant='text' startIcon={<Icon name='arrow-left' />} disabled={isSaving} className={styles.stepIconButton} onClick={onPreviousStep}>
              Voltar
            </Button>
          )}
          <div className={styles.rightButtonGroup}>
            <Button variant='text' disabled={isSaving} onClick={onCloseModal}>
              Cancelar
            </Button>
            <Button disabled={!isStepValid} loading={isSaving} className={styles.stepIconButton} onClick={onNextStep}>
              {steps[currentStepIndex].id !== MediaRequestModalSteps.REQUEST_CONFIRMATION ? (
                <>
                  Próximo
                  <Icon name='arrow-right' size='sm' />
                </>
              ) : (
                <>Solicitar vídeo</>
              )}
            </Button>
          </div>
        </div>
      </div>
      <ConfirmationPopper
        isOpen={!!confirmClosePopperAnchor}
        onCancel={onCancelClosePopper}
        onConfirm={onConfirmClosePopper}
        placement='left-start'
        anchorEl={confirmClosePopperAnchor}
        title='Fechar sem solicitar?'
        bodyText='Cancelando a sua solicitação ela não será realizada. Deseja fechar mesmo assim?'
        confirmTitle='Fechar'
        arrow
      />
    </DialogWithHeader>
  );
});

export default MediaRequestModal;
